import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Switch,
    Typography,
    Box,
    Checkbox,
    List,
    ListItem,
    ListItemText,
    Divider,
    CircularProgress,
    IconButton,
    Paper,
    Avatar,
    Tooltip,
    Fade,
    Zoom,
    Grid
} from "@mui/material";
import { X, Users, Sparkles, CheckCircle2, AlertCircle, Wand2 } from "lucide-react";
import axiosInstance from "../../../../../services/axiosInstance";
import apiEndPointsConfig from "../../../../../services/apiEndPoints";

const AutoAssignSettings = ({ open, onClose, userId }) => {
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [config, setConfig] = useState({ enabled: false, eligibleManagers: [] });
    const [managers, setManagers] = useState([]);
    const [assigningOld, setAssigningOld] = useState(false);
    const [assignResult, setAssignResult] = useState("");

    useEffect(() => {
        if (open && userId) {
            fetchConfig();
            fetchManagers();
        }
    }, [open, userId]);

    const fetchConfig = async () => {
        try {
            setFetching(true);
            const res = await axiosInstance.get(`${apiEndPointsConfig.autoAssignConfig}/${userId}/auto-assign-config`);
            if (res.data.success) {
                setConfig(res.data.data);
            }
        } catch (error) {
            console.error("Error fetching config:", error);
        } finally {
            setFetching(false);
        }
    };

    const fetchManagers = async () => {
        try {
            const res = await axiosInstance.get(`${apiEndPointsConfig.getTeamMembersByClientId}/${userId}`);
            if (res.data.success) {
                const filtered = res.data.data.filter(m => m.role !== "viewer" && m.isActive);
                setManagers(filtered);
            }
        } catch (error) {
            console.error("Error fetching managers:", error);
        }
    };

    const handleToggle = () => {
        setConfig(prev => ({ ...prev, enabled: !prev.enabled }));
    };

    const handleManagerToggle = (managerId) => {
        setConfig(prev => {
            const isEligible = prev.eligibleManagers.includes(managerId);
            const updatedManagers = isEligible
                ? prev.eligibleManagers.filter(id => id !== managerId)
                : [...prev.eligibleManagers, managerId];
            return { ...prev, eligibleManagers: updatedManagers };
        });
    };

    const handleSave = async () => {
        try {
            setLoading(true);
            const res = await axiosInstance.put(`${apiEndPointsConfig.autoAssignConfig}/${userId}/auto-assign-config`, config);
            if (res.data.success) {
                onClose();
            }
        } catch (error) {
            console.error("Error saving config:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleAssignOldLeads = async () => {
        try {
            setAssigningOld(true);
            setAssignResult("");
            const res = await axiosInstance.post(`${apiEndPointsConfig.autoAssignConfig}/${userId}/auto-assign-existing`);
            if (res.data.success) {
                setAssignResult(res.data.message);
                setTimeout(() => setAssignResult(""), 5000);
            }
        } catch (error) {
            console.error("Error assigning old leads:", error);
            setAssignResult("Failed to assign leads.");
        } finally {
            setAssigningOld(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 4,
                    overflow: 'hidden',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                    background: 'linear-gradient(to bottom, #ffffff, #f9fafb)'
                }
            }}
        >
            {/* Header Section with Gradient */}
            <Box sx={{
                p: 3,
                background: 'linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)',
                color: 'white',
                position: 'relative',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <Box display="flex" alignItems="center" gap={2}>
                    <Box sx={{
                        p: 1.2,
                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                        borderRadius: 2.5,
                        backdropFilter: 'blur(8px)',
                        display: 'flex'
                    }}>
                        <Users size={24} color="white" />
                    </Box>
                    <Box>
                        <Typography variant="h6" fontWeight="700" sx={{ lineHeight: 1.2 }}>
                            Auto-Assign Leads
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: 500 }}>
                            Intelligent Round-Robin Distribution
                        </Typography>
                    </Box>
                </Box>
                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{
                        color: 'white',
                        bgcolor: 'rgba(255, 255, 255, 0.1)',
                        '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' }
                    }}
                >
                    <X size={20} />
                </IconButton>
            </Box>

            <DialogContent sx={{ p: 4 }}>
                {fetching ? (
                    <Box display="flex" flexDirection="column" alignItems="center" py={8} gap={2}>
                        <CircularProgress size={40} thickness={4} sx={{ color: '#6366F1' }} />
                        <Typography variant="body2" color="text.secondary" fontWeight="500">
                            Loading your configuration...
                        </Typography>
                    </Box>
                ) : (
                    <Box>
                        {/* Status Toggle Card */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2.5,
                                borderRadius: 3,
                                border: '1px solid #E5E7EB',
                                bgcolor: config.enabled ? 'rgba(99, 102, 241, 0.04)' : '#ffffff',
                                transition: 'all 0.3s ease',
                                mb: 4
                            }}
                        >
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                <Box display="flex" alignItems="center" gap={1.5}>
                                    <Sparkles size={18} color={config.enabled ? "#6366F1" : "#6B7280"} />
                                    <Typography variant="subtitle1" fontWeight="700" color={config.enabled ? "#1F2937" : "#4B5563"}>
                                        Enable Auto-Assignment
                                    </Typography>
                                </Box>
                                <Switch
                                    checked={config.enabled}
                                    onChange={handleToggle}
                                    color="primary"
                                    sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#6366F1' },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#6366F1' }
                                    }}
                                />
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 4, lineHeight: 1.5 }}>
                                When active, all incoming leads from any source will be automatically assigned to your team members in a fair rotation.
                            </Typography>
                        </Paper>

                        {/* Managers Selection Section */}
                        <Box sx={{ mb: 4 }}>
                            <Box display="flex" alignItems="center" gap={1} mb={2}>
                                <Typography variant="subtitle2" fontWeight="800" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6B7280', fontSize: '0.75rem' }}>
                                    Eligible Team Members
                                </Typography>
                                <Box sx={{ flex: 1, height: '1px', bgcolor: '#F3F4F6' }} />
                            </Box>

                            {managers.length === 0 ? (
                                <Box sx={{ py: 4, textAlign: 'center', bgcolor: '#F9FAFB', borderRadius: 3, border: '1px dashed #D1D5DB' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        No account managers found in your team.
                                    </Typography>
                                </Box>
                            ) : (
                                <Grid container spacing={2}>
                                    {managers.map((manager) => {
                                        const isSelected = config.eligibleManagers.includes(manager._id);
                                        return (
                                            <Grid item xs={12} sm={6} key={manager._id}>
                                                <Paper
                                                    elevation={0}
                                                    onClick={() => handleManagerToggle(manager._id)}
                                                    sx={{
                                                        borderRadius: 3,
                                                        border: `2px solid ${isSelected ? '#6366F1' : '#F3F4F6'}`,
                                                        bgcolor: isSelected ? 'rgba(99, 102, 241, 0.04)' : '#ffffff',
                                                        cursor: 'pointer',
                                                        p: 2,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 2,
                                                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                                        position: 'relative',
                                                        '&:hover': {
                                                            borderColor: isSelected ? '#4F46E5' : '#E5E7EB',
                                                            transform: 'translateY(-2px)',
                                                            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                                                        }
                                                    }}
                                                >
                                                    <Checkbox
                                                        checked={isSelected}
                                                        size="small"
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 8,
                                                            right: 8,
                                                            color: '#D1D5DB',
                                                            '&.Mui-checked': { color: '#6366F1' },
                                                            p: 0
                                                        }}
                                                    />
                                                    <Avatar
                                                        sx={{
                                                            width: 44,
                                                            height: 44,
                                                            bgcolor: isSelected ? '#6366F1' : '#F3F4F6',
                                                            color: isSelected ? 'white' : '#6366F1',
                                                            fontWeight: 700,
                                                            fontSize: '0.9rem'
                                                        }}
                                                    >
                                                        {manager.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U'}
                                                    </Avatar>
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography variant="subtitle2" fontWeight="700" color="#1F2937" noWrap>
                                                            {manager.name}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            sx={{
                                                                display: 'block',
                                                                mt: 0.2,
                                                                fontWeight: 500,
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap'
                                                            }}
                                                        >
                                                            {manager.email}
                                                        </Typography>
                                                        <Box sx={{ mt: 0.8, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <Box sx={{
                                                                width: 6,
                                                                height: 6,
                                                                borderRadius: '50%',
                                                                bgcolor: '#10B981'
                                                            }} />
                                                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#6B7280', textTransform: 'capitalize' }}>
                                                                {manager.role || 'Member'}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Paper>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            )}
                        </Box>

                        {/* Retroactive Assignment Tool */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                borderRadius: 4,
                                bgcolor: '#F5F3FF',
                                border: '1px solid #DDD6FE',
                                position: 'relative',
                                overflow: 'hidden'
                            }}
                        >
                            <Box sx={{ position: 'relative', zIndex: 1 }}>
                                <Box display="flex" alignItems="center" gap={1.5} mb={1}>
                                    <Wand2 size={18} color="#8B5CF6" />
                                    <Typography variant="subtitle1" fontWeight="700" color="#5B21B6">
                                        Retroactive Magic
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="#6D28D9" sx={{ mb: 2.5, opacity: 0.8, lineHeight: 1.5 }}>
                                    Have unassigned leads in your bucket? Distribute them instantly among your chosen team members.
                                </Typography>

                                <Box display="flex" alignItems="center" gap={2}>
                                    <Button
                                        onClick={handleAssignOldLeads}
                                        variant="contained"
                                        disabled={assigningOld || config.eligibleManagers.length === 0}
                                        startIcon={assigningOld ? null : <CheckCircle2 size={16} />}
                                        sx={{
                                            bgcolor: '#8B5CF6',
                                            '&:hover': { bgcolor: '#7C3AED' },
                                            textTransform: 'none',
                                            borderRadius: 2,
                                            px: 3,
                                            fontWeight: 600,
                                            boxShadow: '0 4px 14px 0 rgba(139, 92, 246, 0.39)'
                                        }}
                                    >
                                        {assigningOld ? <CircularProgress size={20} color="inherit" /> : "Assign Existing Leads"}
                                    </Button>

                                    <Fade in={!!assignResult}>
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                            {assignResult.includes("Failed") ? <AlertCircle size={14} color="#EF4444" /> : <CheckCircle2 size={14} color="#10B981" />}
                                            <Typography variant="caption" fontWeight="600" color={assignResult.includes("Failed") ? "#EF4444" : "#10B981"}>
                                                {assignResult}
                                            </Typography>
                                        </Box>
                                    </Fade>
                                </Box>
                            </Box>
                            <Box sx={{
                                position: 'absolute',
                                right: -20,
                                top: -20,
                                opacity: 0.05,
                                transform: 'rotate(15deg)'
                            }}>
                                <Sparkles size={120} color="#8B5CF6" />
                            </Box>
                        </Paper>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 3, bgcolor: '#f9fafb', borderTop: '1px solid #E5E7EB' }}>
                <Button
                    onClick={onClose}
                    sx={{
                        color: "#6B7280",
                        textTransform: "none",
                        fontWeight: 600,
                        '&:hover': { bgcolor: '#F3F4F6' }
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={loading || fetching}
                    sx={{
                        bgcolor: "#6366F1",
                        "&:hover": { bgcolor: "#4F46E5" },
                        textTransform: "none",
                        borderRadius: 2.5,
                        px: 4,
                        fontWeight: 700,
                        boxShadow: '0 4px 14px 0 rgba(99, 102, 241, 0.39)'
                    }}
                >
                    {loading ? <CircularProgress size={24} color="inherit" /> : "Save Changes"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AutoAssignSettings;
